import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MdKeyboardArrowDown, MdKeyboardArrowUp, MdArrowBack } from "react-icons/md";

interface FeedbackQuestion {
    QuestionID: string;
    Section: string;
    QuestionText: string;
    Answer: string;
    Type: 'Information' | 'SingleChoice' | 'MultiChoice' | 'OpenText' | 'FileUpload';
    Options?: string[];
}

interface UserFeedback {
    UserID: number;
    UserName: string;
    Email: string;
    IsOptin: boolean;
    OptinFeatureID: number;
    OptinFeatureName: string;
    Answer: FeedbackQuestion[];
    SubmittedDate: string;
    IsSubmitted: boolean;
}

const renderAnswer = (question: FeedbackQuestion) => {
    switch (question.Type) {
        case 'SingleChoice':
            return (
                <div className="d-flex flex-column gap-2">
                    {question.Options?.map((opt, i) => {
                        const isSelected = question.Answer === opt;
                        return (
                            <div key={i} className="d-flex align-items-center gap-3 px-3 py-2 rounded-2 border"
                                style={{ background: isSelected ? '#e0f4f2' : '#fafafa', borderColor: isSelected ? '#00A590' : '#e5e7eb', minHeight: 44 }}>
                                <div className="rounded-circle flex-shrink-0 d-flex align-items-center justify-content-center"
                                    style={{ width: 20, height: 20, border: `2px solid ${isSelected ? '#00A590' : '#bbb'}`, background: isSelected ? '#00A590' : 'white' }}>
                                    {isSelected && <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'white' }} />}
                                </div>
                                <span style={{ fontSize: 14, fontWeight: isSelected ? 600 : 400, color: isSelected ? '#007860' : '#444' }}>{opt}</span>
                            </div>
                        );
                    })}
                </div>
            );
        case 'MultiChoice': {
            const selected = question.Answer ? question.Answer.split(',').map((a: string) => a.trim()) : [];
            return (
                <div className="d-flex flex-column gap-2">
                    {question.Options?.map((opt, i) => {
                        const isSelected = selected.includes(opt);
                        return (
                            <div key={i} className="d-flex align-items-center gap-3 px-3 py-2 rounded-2 border"
                                style={{ background: isSelected ? '#e0f4f2' : '#fafafa', borderColor: isSelected ? '#00A590' : '#e5e7eb', minHeight: 44 }}>
                                <div className="flex-shrink-0 rounded-1 d-flex align-items-center justify-content-center"
                                    style={{ width: 20, height: 20, border: `2px solid ${isSelected ? '#00A590' : '#bbb'}`, background: isSelected ? '#00A590' : 'white' }}>
                                    {isSelected && <span style={{ color: 'white', fontSize: 12, lineHeight: 1 }}>✓</span>}
                                </div>
                                <span style={{ fontSize: 14, fontWeight: isSelected ? 600 : 400, color: isSelected ? '#007860' : '#444' }}>{opt}</span>
                            </div>
                        );
                    })}
                </div>
            );
        }
        case 'OpenText':
            return (
                <div className="px-3 py-3 rounded-2 border" style={{ background: '#f5faf9', color: '#333', whiteSpace: 'pre-wrap', fontSize: 14, borderColor: '#b2dfdb', lineHeight: 1.6 }}>
                    {question.Answer || <span className="text-muted fst-italic">No response provided</span>}
                </div>
            );
        case 'FileUpload':
            return question.Answer ? (
                <div className="mt-1">
                    <img src={question.Answer} alt="Uploaded screenshot" className="rounded-2 border" style={{ maxHeight: 260, maxWidth: '100%', objectFit: 'contain' }} />
                </div>
            ) : <span className="text-muted fst-italic mt-1 d-block" style={{ fontSize: 13 }}>No file uploaded</span>;
        default:
            return null;
    }
};

const groupBySection = (questions: FeedbackQuestion[]): Record<string, FeedbackQuestion[]> => {
    return questions.reduce((acc, q) => {
        if (!acc[q.Section]) acc[q.Section] = [];
        acc[q.Section].push(q);
        return acc;
    }, {} as Record<string, FeedbackQuestion[]>);
};

export default function OptInFeedbackDetail() {
    const navigate = useNavigate();
    const location = useLocation();
    const feedback: UserFeedback = location.state?.feedback;
    const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

    if (!feedback) {
        navigate('/optinFeedback');
        return null;
    }

    const toggleSection = (section: string) => {
        setOpenSections(prev => ({ [section]: !prev[section] }));
    };

    const isSectionOpen = (section: string) => openSections[section] === true;

    const sections = Object.entries(groupBySection(feedback.Answer || []))
        .filter(([, questions]) => questions.some(q => q.Type !== 'Information'));

    return (
        <div className="container-fluid px-3">
            {/* Page header */}
            <div className="d-flex justify-content-between mt-0 w-100">
                <div className="bg-white rounded shadow-sm w-100 d-flex align-items-center p-3 z-intial h-[68px]">
                    <button
                        className="btn p-0 me-3 d-flex align-items-center"
                        onClick={() => navigate('/optinFeedback')}
                        title="Back"
                    >
                        <MdArrowBack size={22} className="text-dark" />
                    </button>
                    <h3 className="fw-bold mb-0">Opt In Feedback</h3>
                </div>
            </div>

            {/* User info strip */}
            <div className="mt-3 rounded shadow-sm border overflow-hidden">
                <div className="d-flex align-items-center justify-content-between px-4 py-3 flex-wrap gap-3" style={{ background: '#E5ECED' }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white flex-shrink-0"
                            style={{ width: 44, height: 44, background: '#00A590', fontSize: 18 }}>
                            {feedback.UserName?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                            <div className="text-muted" style={{ fontSize: 12 }}>Submitted by</div>
                            <div className="fw-bold" style={{ fontSize: 15, color: '#1a3c40' }}>{feedback.UserName}</div>
                            <div style={{ fontSize: 12, color: '#666' }}>{feedback.Email}</div>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-4 flex-wrap">
                        <div className="text-end">
                            <div className="text-muted" style={{ fontSize: 12 }}>Opted Feature</div>
                            <div className="fw-semibold" style={{ fontSize: 14, color: '#00A590' }}>{feedback.OptinFeatureName}</div>
                        </div>
                        <div className="text-end">
                            <div className="text-muted" style={{ fontSize: 12 }}>Submitted Date</div>
                            <div className="fw-semibold" style={{ fontSize: 14 }}>
                                {new Date(feedback.SubmittedDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Accordion sections */}
            <div className="mt-3">
                {sections.length === 0 ? (
                    <div className="bg-white rounded shadow-sm border p-4 text-center text-muted">No feedback answers available.</div>
                ) : (
                    sections.map(([section, questions]) => {
                        const isOpen = isSectionOpen(section);
                        const visibleQuestions = questions.filter(q => q.Type !== 'Information');
                        return (
                            <div key={section} className="mb-3 rounded shadow-sm border overflow-hidden">
                                {/* Accordion header */}
                                <div
                                    className="d-flex align-items-center justify-content-between px-4 py-3 cursor-pointer"
                                    style={{ background: isOpen ? '#00A590' : '#fff', borderBottom: isOpen ? '1px solid #009080' : 'none' }}
                                    onClick={() => toggleSection(section)}
                                >
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="flex-shrink-0 rounded" style={{ width: 4, height: 18, background: isOpen ? 'rgba(255,255,255,0.7)' : '#00A590' }} />
                                        <h6 className="fw-bold mb-0" style={{ color: isOpen ? '#fff' : '#1a3c40', fontSize: 14 }}>{section}</h6>
                                        <span className="ms-2 rounded-pill px-2 py-0" style={{ background: isOpen ? 'rgba(255,255,255,0.2)' : '#e0f4f2', color: isOpen ? '#fff' : '#00A590', fontSize: 11 }}>
                                            {visibleQuestions.length} questions
                                        </span>
                                    </div>
                                    {isOpen
                                        ? <MdKeyboardArrowUp size={22} color="#fff" />
                                        : <MdKeyboardArrowDown size={22} color="#00A590" />
                                    }
                                </div>

                                {/* Accordion body */}
                                {isOpen && (
                                    <div className="d-flex flex-column gap-3 p-4" style={{ background: '#fafafa' }}>
                                        {visibleQuestions.map((q, i) => (
                                            <div key={i} className="rounded-3 p-4 border bg-white" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                                                <p className="fw-semibold mb-3 d-flex align-items-start gap-2" style={{ color: '#1a3c40', fontSize: 14, lineHeight: 1.5 }}>
                                                    <span className="flex-shrink-0 fw-bold" style={{ color: '#00A590' }}>{q.QuestionID}.</span>
                                                    <span>{q.QuestionText}</span>
                                                </p>
                                                {renderAnswer(q)}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
